import React, { useEffect, useRef } from 'react';

import type { PayPalButtonsComponent } from '@paypal/paypal-js';
import type { PayPalButtonsComponentProps } from '@paypal/react-paypal-js';

import { cn } from '@change/ds-utils';

import { getWindow } from '@change-corgi/core/window';

import { PaypalButtonLoading } from '../PaypalButtonLoading';

type Props = PayPalButtonsComponentProps & {
	loading?: boolean;
};

export function PaypalButtonSDK({ loading, ...props }: Props): React.JSX.Element | null {
	const window = getWindow();
	const containerRef = useRef<HTMLDivElement>(null);
	const buttonsRef = useRef<PayPalButtonsComponent | null>(null);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (!window.paypal || !containerRef.current || buttonsRef.current) return undefined;

		const buttons = window.paypal.Buttons(props);
		buttonsRef.current = buttons;
		void buttons.render(containerRef.current);

		return () => {
			buttonsRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.paypal]);

	useEffect(() => {
		if (!buttonsRef.current) return;
		void buttonsRef.current.updateProps(props);
	}, [props]);

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (!window.paypal) return null;

	// Box with negative margin here to remove extra padding added by paypal sdk
	return (
		<div className="mb-[-5px]" data-qa="paypal-button-sdk-native">
			{loading && <PaypalButtonLoading style={props.style} />}
			<div
				className={cn({ hidden: loading })}
				data-testid="paypal-driver-button-wrapper"
				data-qa="paypal-submit-button"
			>
				<div ref={containerRef} />
			</div>
		</div>
	);
}
